import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, ReplaySubject} from "rxjs";

import {
  DobryAgentInsuranceCompany,
  FacilityDobryAgent, OpeningHoursDaysOrder,
  OpeningHoursViewItem,
  PaymentMethod,
  SubCategory
} from "../../../../shared/interface/facility-dobry-agent";
import {AuthService} from "../../../../shared/services/auth.service";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {  takeUntil} from "rxjs/operators";
import {PartnerFacility} from "../../../../shared/interface/partner-facility";
import {environment} from "../../../../../environments/environment";


@Component({
  selector: 'app-dobry-agent-facility-view',
  templateUrl: './dobry-agent-facility-view.component.html',
  styleUrls: ['./dobry-agent-facility-view.component.scss']
})
export class DobryAgentFacilityViewComponent implements OnInit, OnDestroy {
  @Input() facility: Observable<PartnerFacility>;
  @Input() facilityDobryAgent: Observable<FacilityDobryAgent>;
  @Output() modifyClicked = new EventEmitter<any>();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  partnerFacility?: PartnerFacility = null;
  facilityDobryAgentObject?: FacilityDobryAgent = null;
  userPermissions: ReplaySubject<UserPermissions>;

  readonly openingHoursDaysOrder = OpeningHoursDaysOrder;
  constructor(
    private auth: AuthService,
  ) {
    this.userPermissions = auth.getUserPermissions();

  }

  ngOnInit(): void {
    this.facility
      .pipe(takeUntil(this.destroyed$))
      .subscribe((facility) => {
        this.partnerFacility = facility;
      });
    this.facilityDobryAgent
      .pipe(takeUntil(this.destroyed$))
      .subscribe((facilityDobryAgent: FacilityDobryAgent) => {
        this.facilityDobryAgentObject = facilityDobryAgent;
      })

  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  modify(): void {
    this.modifyClicked.emit();
  }


  getOpeningHoursItems(): OpeningHoursViewItem[] {
    const openHours = this.facilityDobryAgentObject?.openHours;

    if (!openHours) {
      return [];
    }

    return this.openingHoursDaysOrder.map((day) => {
      const item = openHours[day.key];

      const openTime = item?.openTime ?? '';
      const closeTime = item?.closeTime ?? '';

      const closed =
        openTime.toLowerCase() === 'nieczynne'
        || closeTime.toLowerCase() === 'nieczynne'
        || (!openTime && !closeTime);

      return {
        key: day.key,
        label: day.label,
        openTime,
        closeTime,
        closed,
      };
    });
  }
  getActivePaymentMethods(): PaymentMethod[] {
    return this.facilityDobryAgentObject?.paymentMethods
      ?.filter((method: PaymentMethod) => method.active) ?? [];
  }

  getPaymentMethodIcon(method: PaymentMethod): string {
    if (method.icon) {
      return method.icon;
    }

    switch (method.slug) {
      case 'blik':
        return 'smartphone';
      case 'card':
        return 'credit_card';
      case 'cash':
        return 'payments';
      case 'transfer':
        return 'account_balance';
      default:
        return 'payment';
    }
  }


  getActiveSubCategories(): SubCategory[] {
    return this.facilityDobryAgentObject?.subCategories
      ?.filter((subCategory: SubCategory) => {
        return subCategory.active !== false;
      })
      ?.sort((a: SubCategory, b: SubCategory) => {
        const positionA = a.position ?? 0;
        const positionB = b.position ?? 0;

        if (positionA !== positionB) {
          return positionA - positionB;
        }

        return (a.name ?? '').localeCompare(b.name ?? '');
      }) ?? [];
  }

  getInsuranceCompanies(): DobryAgentInsuranceCompany[] {
    return this.facilityDobryAgentObject?.insuranceCompanies
      ?.sort((a: DobryAgentInsuranceCompany, b: DobryAgentInsuranceCompany) => {
        return (a.name ?? '').localeCompare(b.name ?? '');
      }) ?? [];
  }

  getChronieUrl(url?: string | null): string | null {
    if (!url) {
      return null;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    return `https://${url}`;
  }


  getImageUrl(url?: string | null): string | null {
    if (!url) {
      return null;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    return `${environment.dobryAgentUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
  }

  getImageSrcset(srcset?: string | null): string | null {
    if (!srcset) {
      return null;
    }

    return srcset
      .split(',')
      .map((item: string) => {
        const trimmed = item.trim();

        if (!trimmed) {
          return '';
        }

        const parts = trimmed.split(/\s+/);
        const url = parts[0];
        const descriptor = parts.slice(1).join(' ');

        const fullUrl = this.getImageUrl(url);

        return descriptor ? `${fullUrl} ${descriptor}` : fullUrl;
      })
      .filter(Boolean)
      .join(', ');
  }


}
